import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { BookableSlotWindowRequest } from "../../features/bookable-slots/contracts/BookableSlotWindowRequest";
import { WEEK_DAYS, WeekDay } from "../../features/bookable-slots/enums/WeekDay";
import { BookingMode } from "../../features/sessions/enums/BookingMode";
import { TopicRef } from "../../features/training/models/TopicPlan";

interface BookableSlotWindowRowProps {
  index: number;
  window: BookableSlotWindowRequest;
  topics: TopicRef[];
  onChange: (window: BookableSlotWindowRequest) => void;
  onRemove: () => void;
}

const UNITS = [45, 90];
const MODES = [BookingMode.OpenWithConfirmation, BookingMode.OpenAutoConfirm];

/** 2027-01-04 is a Monday: a fixed week to print localized weekday names from. */
const weekdayName = (day: WeekDay, locale: string): string =>
  new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" }).format(
    new Date(Date.UTC(2027, 0, 4 + WEEK_DAYS.indexOf(day))),
  );

/** One editable weekly window: day, from–to, unit, mode and an optional default practical topic. */
const BookableSlotWindowRow = ({ index, window, topics, onChange, onRemove }: BookableSlotWindowRowProps) => {
  const { t, i18n } = useTranslation();
  const id = (field: string) => `slot-window-${index}-${field}`;

  return (
    <li className="flex flex-wrap items-end gap-3 rounded-2xl bg-grey-95 p-3 dark:bg-primary-20">
      <label className="label w-full sm:w-auto sm:min-w-[9rem] sm:flex-1" htmlFor={id("day")}>
        <span className="label-text">{t("timeSlots.bookable.day")}</span>
        <select
          id={id("day")}
          className="input w-full"
          value={window.dayOfWeek}
          onChange={(e) => onChange({ ...window, dayOfWeek: e.target.value as WeekDay })}
        >
          {WEEK_DAYS.map((day) => (
            <option key={day} value={day}>
              {weekdayName(day, i18n.language)}
            </option>
          ))}
        </select>
      </label>
      <label className="label w-full sm:w-auto sm:min-w-[9rem] sm:flex-1" htmlFor={id("from")}>
        <span className="label-text">{t("timeSlots.bookable.from")}</span>
        <input
          id={id("from")}
          type="time"
          className="input w-full"
          value={window.startLocal}
          onChange={(e) => onChange({ ...window, startLocal: e.target.value })}
        />
      </label>
      <label className="label w-full sm:w-auto sm:min-w-[9rem] sm:flex-1" htmlFor={id("to")}>
        <span className="label-text">{t("timeSlots.bookable.to")}</span>
        <input
          id={id("to")}
          type="time"
          className="input w-full"
          value={window.endLocal}
          onChange={(e) => onChange({ ...window, endLocal: e.target.value })}
        />
      </label>
      <label className="label w-full sm:w-auto sm:min-w-[9rem] sm:flex-1" htmlFor={id("unit")}>
        <span className="label-text">{t("timeSlots.bookable.unit")}</span>
        <select
          id={id("unit")}
          className="input w-full"
          value={window.unitMinutes}
          onChange={(e) => onChange({ ...window, unitMinutes: Number(e.target.value) })}
        >
          {UNITS.map((unit) => (
            <option key={unit} value={unit}>
              {unit} {t("portal.minutes", "min")}
            </option>
          ))}
        </select>
      </label>
      <label className="label w-full sm:w-auto sm:min-w-[15rem] sm:flex-[2]" htmlFor={id("mode")}>
        <span className="label-text">{t("timeSlots.bookable.modeLabel")}</span>
        <select
          id={id("mode")}
          className="input w-full"
          value={window.bookingMode}
          onChange={(e) => onChange({ ...window, bookingMode: e.target.value as BookingMode })}
        >
          {MODES.map((mode) => (
            <option key={mode} value={mode}>
              {t(`timeSlots.bookable.mode.${mode}`)}
            </option>
          ))}
        </select>
      </label>
      <div className="flex w-full items-end gap-2 sm:w-auto sm:min-w-[18rem] sm:flex-[2]">
        <label className="label min-w-0 flex-1" htmlFor={id("topic")}>
          <span className="label-text">{t("timeSlots.bookable.defaultTopic")}</span>
          <select
            id={id("topic")}
            className="input w-full"
            value={window.defaultTopicId ?? ""}
            onChange={(e) => onChange({ ...window, defaultTopicId: e.target.value || null })}
          >
            <option value="">{t("timeSlots.bookable.noTopic")}</option>
            {topics.map((topic) => (
              <option key={topic.topicId} value={topic.topicId}>
                {`${topic.code} · ${topic.title}`}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="btn btn-secondary"
          aria-label={t("timeSlots.bookable.remove")}
          title={t("timeSlots.bookable.remove")}
          onClick={onRemove}
        >
          <X size={18} />
        </button>
      </div>
    </li>
  );
};

export default BookableSlotWindowRow;
